import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "chibah_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      console.warn("[cart] could not read localStorage:", err);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("[cart] could not persist to localStorage:", err);
    }
  }, [items]);

  const add = useCallback((product, quantity = 1, opts = {}) => {
    const variants = product.variants || [];
    // Caller may pass explicit { variantId } — falls back to first available.
    const explicit = opts.variantId ? variants.find((v) => v.id === opts.variantId) : null;
    const variant = explicit || variants.find((v) => v.available) || variants[0];
    const variantKey = variant?.id || null;
    // Distinct cart line per variant so size S and size M coexist
    const lineKey = variantKey ? `${product.id}::${variantKey}` : product.id;

    setItems((curr) => {
      const found = curr.find((i) => i.line_key === lineKey);
      if (found) {
        return curr.map((i) =>
          i.line_key === lineKey ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      const unitPrice =
        typeof variant?.price === "number"
          ? variant.price
          : typeof product.price === "number"
          ? product.price
          : 0;
      return [
        ...curr,
        {
          line_key: lineKey,
          product_id: product.id,
          variant_id: variantKey,
          variant_title:
            variant?.title && variant.title !== "Default Title" ? variant.title : null,
          name: product.name || product.title,
          price: unitPrice,
          image: product.image,
          slug: product.slug || product.handle,
          quantity,
        },
      ];
    });
  }, []);

  const update = useCallback((line_key, quantity) => {
    if (quantity <= 0) {
      setItems((curr) => curr.filter((i) => (i.line_key || i.product_id) !== line_key));
    } else {
      setItems((curr) =>
        curr.map((i) => ((i.line_key || i.product_id) === line_key ? { ...i, quantity } : i))
      );
    }
  }, []);

  const remove = useCallback((line_key) => {
    setItems((curr) => curr.filter((i) => (i.line_key || i.product_id) !== line_key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, add, update, remove, clear, total, count }),
    [items, add, update, remove, clear, total, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
